import { HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from "rxjs";
import { UiMessageService } from "./ui-message-broker/ui-message-broker.service";

@Injectable({ providedIn: 'root' })
export class AppInterceptorService implements HttpInterceptor {

  // Single-flight refresh: only one /oauth2/refreshtoken call in flight.
  // Concurrent failing requests subscribe to refreshSubject and replay once
  // the in-flight refresh completes.
  private refreshInProgress = false;
  private refreshSubject = new BehaviorSubject<boolean | null>(null);

  private uiMessageService: UiMessageService = inject(UiMessageService);

  constructor(private http: HttpClient) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // Callers can opt out of the refresh/redirect cycle by setting this
    // header (e.g. probes that legitimately 401).
    const skipAuthRefresh = req.headers.get('X-Skip-Auth-Refresh') === 'true';
    let headers = req.headers.delete('X-Skip-Auth-Refresh');

    // Always attach the UI session id so backend services can put it in the
    // SLF4J MDC (nucleus-core's UiSessionMdcFilter sets the "uiSessionId" MDC
    // key) — lets operators filter logs across services for one browser tab.
    // Don't overwrite an explicit value: some callers build the header
    // themselves and that pathway must stay intact.
    if (!headers.has('X-Ui-Session-Id')) {
      headers = headers.set('X-Ui-Session-Id', AppInterceptorService.getOrCreateSessionId());
    }

    // BFF: access_token is an HttpOnly cookie attached automatically when
    // withCredentials is set. The Express proxy reads the cookie and injects
    // Authorization: Bearer for upstream services.
    const request = req.clone({ headers, withCredentials: true });

    return next.handle(request).pipe(
      catchError(err => {

        // NUC-86: gateway forced-rejection codes — JWT/user is permanently
        // dead, refresh would loop. Go straight to login. Match against the
        // structured-error envelope { code, message } returned by
        // ReverseProxyFilter on blacklist hits.
        const code = err?.error?.code;
        if (code === 'TOKEN_REVOKED' || code === 'USER_BLOCKED' || code === 'IP_BLOCKED') {
          window.location.assign('/oauth2/authenticate');
          return throwError(() => err);
        }

        // Not auth-related → propagate. Some Spring Security configs return
        // 403 (instead of 401) for an expired access token, so we treat both
        // as recoverable.
        if (err.status !== 401 && err.status !== 403) {
          return throwError(() => err);
        }

        // Caller opted out of the refresh dance.
        if (skipAuthRefresh) {
          return throwError(() => err);
        }

        // Never refresh for auth/session endpoints — that would loop.
        if (this.isAuthEndpoint(req.url)) {
          return throwError(() => err);
        }

        // Refresh already running → wait for it, then replay. If the refresh
        // failed, surface the original error in-app (no hard redirect).
        if (this.refreshInProgress) {
          return this.refreshSubject.pipe(
            filter(v => v !== null),
            take(1),
            switchMap(ok => ok
              ? next.handle(req.clone({ withCredentials: true }))
              : throwError(() => err))
          );
        }

        // Start a refresh.
        this.refreshInProgress = true;
        this.refreshSubject.next(null);

        return this.refreshToken().pipe(
          switchMap(() => {
            this.refreshInProgress = false;
            this.refreshSubject.next(true);
            // New access_token cookie is live — re-open the SSE stream so it
            // reconnects with fresh credentials.
            this.uiMessageService.reconnect();
            // Re-clone so the refreshed cookie is read on the replay.
            return next.handle(req.clone({ withCredentials: true }));
          }),
          catchError(() => {
            this.refreshInProgress = false;
            this.refreshSubject.next(false);
            // A failed refresh on a single request must NOT eject the user
            // from the SPA onto a raw backend error page (the OAuth flow can
            // render an <ErrorEntity> XML response). Propagate the original
            // error so the caller shows an in-app message; the user stays in
            // the app and can refresh the page to re-authenticate.
            return throwError(() => err);
          })
        );
      })
    );
  }

  private refreshToken(): Observable<any> {
    return this.http.post(
      '/oauth2/refreshtoken',
      new URLSearchParams({ grant_type: 'refresh_token' }),
      { withCredentials: true }
    );
  }

  private isAuthEndpoint(url: string): boolean {
    return (
      url.includes('/auth/me') ||
      url.includes('/oauth2/refreshtoken') ||
      url.includes('/oauth2/logout') ||
      url.includes('/oauth2/authorize') ||
      url.includes('/login')
    );
  }

  /** Stable per-browser id persisted in localStorage. Generated on first
   *  outgoing request if the login flow hasn't populated one yet. Kept on a
   *  class static so every consumer (interceptor, monitoring service, any
   *  future diagnostic hook) reads/writes the same slot. */
  static getOrCreateSessionId(): string {
    let sid = localStorage.getItem('uiSessionId');
    if (!sid) {
      sid = 'ui-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now();
      localStorage.setItem('uiSessionId', sid);
    }
    return sid;
  }
}
